import { MultiSelectCombobox } from '@synoptic/ui-kit/multi-select-combobox/multi-select-combobox.js';
import { MultiSelectMenuContainer } from '@synoptic/ui-kit/multi-select-combobox/multi-select-group.tsx';
import { useMemo } from 'react';
import { Stream } from '@/__generated__/schema-types';
import { PaginatedMultiSelectResult } from './paginated-multiselect-result';
import { StreamListItem } from './stream-list-item';
import { StreamPill } from './stream-pill';
import { useTypeaheadStreams } from './use-collection-typeahead';

const isItemDisabled = (item: Stream) => !item.canAddToStreamDeck;

export type TypeaheadMultiselectProps = {
  selectedSources: Stream[];
  errorMessage?: string;
  onSourcesChange: (sources: Stream[]) => void;
};
export const TypeaheadMultiselect = ({
  selectedSources,
  errorMessage,
  onSourcesChange,
}: TypeaheadMultiselectProps) => {
  const {
    onValueChange: onStreamValueChange,
    streams,
    isLoading: streamsLoading,
  } = useTypeaheadStreams();

  const dropdownItems = useMemo(() => {
    if (!streams) {
      return null;
    }
    return (streams as Stream[]).filter(
      (item) => !selectedSources.find((v) => v.id === item.id),
    );
  }, [selectedSources, streams]);

  return (
    <MultiSelectCombobox<Stream>
      placeholder="ex. US Federal Court Cases"
      dropdownItems={dropdownItems}
      onInputChange={(inputValue) => {
        onStreamValueChange(inputValue || '');
      }}
      errorMessage={errorMessage}
      label="Select the streams you want to add to the list"
      selectedItems={selectedSources}
      setSelectedItems={(v) => {
        if (v) {
          onSourcesChange(v);
        }
      }}
      isLoading={streamsLoading}
      isItemSelected={(item) => !!selectedSources.find((v) => v.id === item.id)}
      getItemId={(item) => item.id}
      isItemDisabled={isItemDisabled}
      renderDropdownItems={({ getItemProps }) => {
        return (
          <MultiSelectMenuContainer>
            <PaginatedMultiSelectResult<Stream>
              emptyPlaceholder="No streams found"
              groupName="Streams"
              showLabel={false}
              items={streams}
              selectedItems={selectedSources}
              getItemProps={(options) =>
                getItemProps({
                  ...options,
                  'data-testid': 'stream-list-item',
                })
              }
              isItemDisabled={isItemDisabled}
              isInitialLoading={streamsLoading}
              showMaxItemsHint={true}
            >
              {(item) => <StreamListItem stream={item} />}
            </PaginatedMultiSelectResult>
          </MultiSelectMenuContainer>
        );
      }}
      renderItemPill={(source, onRemove) => (
        <StreamPill stream={source} onRemove={onRemove} />
      )}
    />
  );
};
